import { ColorPicker, Divider } from 'antd';
import type { ColorPickerProps } from 'antd';
import { useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { DomUtil } from 'src/util/DomUtil';
import './XmColorPicker.less';

const POPUP_CLASS = 'xm-color-picker-popup';
const POPUP_CLASSNAMES: ColorPickerProps['classNames'] = { popup: { root: POPUP_CLASS } };
const POPUP_STYLES: ColorPickerProps['styles'] = { popupOverlayInner: { padding: 10 } };
const ARROW_AT_CENTER: ColorPickerProps['arrow'] = { pointAtCenter: true };

const SKETCH_DEFAULT: readonly string[] = [
  '#d0021b', '#f5a623', '#f8e71c', '#8b572a', '#7ed321', '#417505', '#e91e63', '#bd10e0',
  '#9013fe', '#4a90e2', '#50e3c2', '#b8e986', '#000000', '#4a4a4a', '#9b9b9b', '#ffffff',
];

interface XmColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  colors?: readonly string[];
  mode?: 'full' | 'presets';
  children?: ReactElement;
}

export default function XmColorPicker({
  value,
  onChange,
  colors = SKETCH_DEFAULT,
  mode = 'full',
  children,
}: Readonly<XmColorPickerProps>): ReactElement {
  const [open, setOpen] = useState(false);

  const pick = (hex: string): void => {
    onChange(hex);
    if (mode === 'presets') setOpen(false);
  };

  const active = value?.toLowerCase();
  const swatches = (
    <div className="xm-cp-swatches">
      {colors.map((c, i) => (
        <button
          key={`${i}-${c}`}
          type="button"
          aria-label={c}
          className={active === c.toLowerCase() ? 'xm-cp-swatch xm-cp-swatch-active' : 'xm-cp-swatch'}
          style={{ background: c, '--sw': c } as CSSProperties}
          onClick={() => pick(c)}
        />
      ))}
    </div>
  );

  const panelRender: ColorPickerProps['panelRender'] = (_, { components: { Picker } }) =>
    mode === 'presets'
      ? <div className="xm-cp-panel">{swatches}</div>
      : (
        <div className="xm-cp-panel">
          <Picker />
          <Divider className="xm-cp-divider" />
          {swatches}
        </div>
      );

  const trigger = children ?? (
    <span className="xm-cp-trigger">
      <span className="xm-cp-trigger-swatch" style={{ background: value }} />
    </span>
  );

  return (
    <ColorPicker
      value={value}
      open={open}
      onOpenChange={setOpen}
      onChange={color => onChange(color.toHexString())}
      disabledAlpha
      disabledFormat
      arrow={ARROW_AT_CENTER}
      panelRender={panelRender}
      getPopupContainer={DomUtil.popupToBody}
      classNames={POPUP_CLASSNAMES}
      styles={POPUP_STYLES}
    >
      {trigger}
    </ColorPicker>
  );
}
